import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Menu } from '@headlessui/react'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  ShoppingCartIcon,
  CheckCircleIcon,
  ClockIcon,
  PrinterIcon,
  ChevronDownIcon,
  ReceiptRefundIcon,
  TruckIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline'
import { saleService } from '../../services/saleService'
import { invoiceService } from '../../services/invoiceService'
import { ReceiptModal } from '../../components/sales/ReceiptModal'
import { DeliveryNoteModal } from '../../components/sales/DeliveryNoteModal'
import { InvoiceModal } from '../../components/invoices/InvoiceModal'
import toast from 'react-hot-toast'

interface Sale {
  sale_id: number
  sale_number: string
  sale_date: string
  sale_type: string
  total_amount: number
  paid_amount: number
  balance_due: number
  status: string
  customers: {
    customer_id: number
    company_name: string | null
    first_name: string | null
    last_name: string | null
    customer_groups: { group_name: string } | null
  } | null
  sale_items: { item_id: number }[]
}

export const SalesPage = () => {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [page, setPage] = useState(1)
  const [printSale, setPrintSale] = useState<any | null>(null)
  const [printMode, setPrintMode] = useState<'receipt' | 'delivery' | null>(null)
  const [loadingPrintId, setLoadingPrintId] = useState<number | null>(null)
  const [invoicePreview, setInvoicePreview] = useState<any | null>(null)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, statusFilter])

  const { data, isLoading } = useQuery({
    queryKey: ['sales', debouncedSearch, statusFilter, page],
    queryFn: () => saleService.getAll({ search: debouncedSearch || undefined, status: statusFilter, page, limit: 20 }),
  })

  const { data: todayData } = useQuery({
    queryKey: ['sales', 'today'],
    queryFn: () => saleService.getTodaySales(),
  })

  const sales: Sale[] = data?.data?.data?.items || []
  const pagination = data?.data?.data?.pagination
  const todaySummary = todayData?.data?.data?.summary || { count: 0, total: 0, paid: 0 }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800'
      case 'confirmed': return 'bg-blue-100 text-blue-800'
      case 'partially_paid': return 'bg-yellow-100 text-yellow-800'
      case 'draft': return 'bg-gray-100 text-gray-800'
      case 'cancelled': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getCustomerName = (sale: Sale) => {
    if (!sale.customers) return 'Walk-in Customer'
    if (sale.customers.company_name) return sale.customers.company_name
    return `${sale.customers.first_name || ''} ${sale.customers.last_name || ''}`.trim()
  }

  const handlePrint = async (sale: Sale, mode: 'receipt' | 'delivery') => {
    setLoadingPrintId(sale.sale_id)
    try {
      const response = await saleService.getById(sale.sale_id)
      setPrintSale(response.data?.data)
      setPrintMode(mode)
    } catch {
      toast.error('Failed to load sale details')
    } finally {
      setLoadingPrintId(null)
    }
  }

  const closePrintModal = () => {
    setPrintMode(null)
    setPrintSale(null)
  }

  const handlePreviewInvoice = async (sale: Sale) => {
    setLoadingPrintId(sale.sale_id)
    try {
      const response = await invoiceService.getBySale(sale.sale_id)
      setInvoicePreview(response.data?.data)
    } catch {
      toast.error('No invoice found for this sale')
    } finally {
      setLoadingPrintId(null)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sales</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage sales transactions and payments
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/pos"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <PlusIcon className="w-5 h-5" />
            New Sale
          </Link>
        </div>
      </div>

      {/* Today's Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-50 rounded-lg">
              <ShoppingCartIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Today's Sales</p>
              <p className="text-2xl font-bold text-gray-900">{todaySummary.count}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-50 rounded-lg">
              <CheckCircleIcon className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Today's Revenue</p>
              <p className="text-2xl font-bold text-gray-900">
                KSh {todaySummary.total.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-50 rounded-lg">
              <ClockIcon className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Payments Received</p>
              <p className="text-2xl font-bold text-gray-900">
                KSh {todaySummary.paid.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search sales..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="confirmed">Confirmed</option>
            <option value="partially_paid">Partially Paid</option>
            <option value="paid">Paid</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Sales List */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : sales.length === 0 ? (
        <div className="bg-white rounded-xl shadow border border-gray-100 p-12 text-center">
          <ShoppingCartIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No sales found</h3>
          <p className="text-gray-500 mb-4">
            {debouncedSearch || statusFilter
              ? 'Try adjusting your search or filters'
              : 'Get started by creating your first sale'}
          </p>
          {!debouncedSearch && !statusFilter && (
            <Link
              to="/pos"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <PlusIcon className="w-5 h-5" />
              New Sale
            </Link>
          )}
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sale #</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {sales.map((sale) => (
                    <tr key={sale.sale_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <Link 
                          to={`/sales/${sale.sale_id}`}
                          className="text-sm font-medium text-primary-600 hover:underline"
                        >
                          {sale.sale_number}
                        </Link>
                        <p className="text-xs text-gray-500 capitalize">{sale.sale_type}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">{getCustomerName(sale)}</p>
                        <p className="text-xs text-gray-500">
                          {sale.customers?.customer_groups?.group_name || 'Retail'}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {new Date(sale.sale_date).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {sale.sale_items.length} items
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">
                          KSh {Number(sale.total_amount).toLocaleString()}
                        </p>
                        {sale.balance_due > 0 && (
                          <p className="text-xs text-yellow-600">
                            Balance: KSh {Number(sale.balance_due).toLocaleString()}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(sale.status)}`}>
                          {sale.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/sales/${sale.sale_id}`}
                            className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                          >
                            View
                          </Link>
                          <Menu as="div" className="relative">
                            <Menu.Button
                              disabled={loadingPrintId === sale.sale_id}
                              className="flex items-center gap-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                              title="Print"
                            >
                              <PrinterIcon className="w-4 h-4" />
                              <ChevronDownIcon className="w-3 h-3" />
                            </Menu.Button>
                            <Menu.Items className="absolute right-0 z-10 mt-1 w-52 origin-top-right rounded-lg bg-white shadow-lg ring-1 ring-black/5 focus:outline-none">
                              <div className="py-1">
                                <Menu.Item>
                                  {({ active }) => (
                                    <button
                                      onClick={() => handlePrint(sale, 'receipt')}
                                      className={`flex items-center gap-2 w-full px-4 py-2 text-sm text-left ${
                                        active ? 'bg-gray-50 text-gray-900' : 'text-gray-700'
                                      }`}
                                    >
                                      <ReceiptRefundIcon className="w-4 h-4 text-gray-400" />
                                      Print Receipt
                                    </button>
                                  )}
                                </Menu.Item>
                                <Menu.Item>
                                  {({ active }) => (
                                    <button
                                      onClick={() => handlePrint(sale, 'delivery')}
                                      className={`flex items-center gap-2 w-full px-4 py-2 text-sm text-left ${
                                        active ? 'bg-gray-50 text-gray-900' : 'text-gray-700'
                                      }`}
                                    >
                                      <TruckIcon className="w-4 h-4 text-gray-400" />
                                      Print Delivery Note
                                    </button>
                                  )}
                                </Menu.Item>
                                <Menu.Item>
                                  {({ active }) => (
                                    <button
                                      onClick={() => handlePreviewInvoice(sale)}
                                      className={`flex items-center gap-2 w-full px-4 py-2 text-sm text-left ${
                                        active ? 'bg-gray-50 text-gray-900' : 'text-gray-700'
                                      }`}
                                    >
                                      <DocumentTextIcon className="w-4 h-4 text-gray-400" />
                                      Preview Invoice
                                    </button>
                                  )}
                                </Menu.Item>
                              </div>
                            </Menu.Items>
                          </Menu>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-between bg-white rounded-xl shadow border border-gray-100 p-4">
              <p className="text-sm text-gray-500">
                Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} sales
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                  disabled={page === pagination.pages}
                  className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <ReceiptModal isOpen={printMode === 'receipt'} onClose={closePrintModal} sale={printSale} />
      <DeliveryNoteModal isOpen={printMode === 'delivery'} onClose={closePrintModal} sale={printSale} />
      {invoicePreview && (
        <InvoiceModal isOpen={!!invoicePreview} onClose={() => setInvoicePreview(null)} invoice={invoicePreview} />
      )}
    </div>
  )
}
